<template>
  <div class="news-page">
    <!-- Hero Banner -->
    <section class="hero-banner">
      <div class="hero-content">
        <p class="hero-label">新闻公告</p>
        <h1 class="hero-title">最新动态</h1>
        <p class="hero-subtitle">产品更新、行业资讯、公司动态</p>
      </div>
    </section>

    <!-- Main Content -->
    <div class="page-container">
      <!-- Filter Tabs -->
      <div class="filter-tabs">
        <button
          v-for="tab in filterTabs"
          :key="tab.key"
          class="filter-tab"
          :class="{ active: activeFilter === tab.key }"
          @click="activeFilter = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- News List -->
      <div class="news-list">
        <div
          v-for="item in filteredNews"
          :key="item.id"
          class="news-item"
          @click="handleNewsClick(item)"
        >
          <div class="news-date-block">
            <p class="news-day">{{ item.day }}</p>
            <p class="news-month">{{ item.month }}</p>
          </div>
          <div class="news-content">
            <h4 class="news-title">{{ item.title }}</h4>
            <p class="news-summary">{{ item.summary }}</p>
            <div class="news-footer">
              <span class="news-date-tag">{{ item.date }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { newsItems, type NewsItem } from '../data/portalData'
import { getPortalArticlesByCategory } from '@/api/portal'

defineOptions({ name: 'NewsPage' })

const router = useRouter()
const activeFilter = ref<string>('all')
const displayNews = ref<NewsItem[]>(newsItems)

onMounted(async () => {
  try {
    const articles = await getPortalArticlesByCategory('portal-news')
    if (articles.length) {
      displayNews.value = articles.map((article) => {
        let extra: Record<string, string> = {}
        try {
          extra = JSON.parse(article.extra || '{}')
        } catch {}
        const date = article.publishTime ? new Date(article.publishTime) : new Date()
        return {
          id: String(article.id),
          title: article.title,
          summary: article.summary || '',
          category: (extra.category as NewsItem['category']) || 'company',
          date: date.toLocaleDateString('zh-CN'),
          day: String(date.getDate()).padStart(2, '0'),
          month: `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}`,
          content: article.content || ''
        }
      })
    }
  } catch {
    // CMS 未配置或暂不可用时保留当前门户内容。
  }
})

const filterTabs = [
  { key: 'all', label: '全部' },
  { key: 'product', label: '产品更新' },
  { key: 'company', label: '公司动态' },
  { key: 'industry', label: '行业资讯' }
]

const filteredNews = computed<NewsItem[]>(() => {
  if (activeFilter.value === 'all') {
    return displayNews.value
  }
  return displayNews.value.filter((item) => item.category === activeFilter.value)
})

function handleNewsClick(item: NewsItem) {
  router.push(`/portal/news/${item.id}`)
}
</script>

<style lang="scss" scoped>
.news-page {
  // Hero Banner
  .hero-banner {
    height: 240px;
    background: linear-gradient(135deg, #111827, #374151);
    display: flex;
    align-items: center;
    padding: 0 24px;
  }

  .hero-content {
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
  }

  .hero-label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
    letter-spacing: 4px;
    margin: 0 0 8px;
  }

  .hero-title {
    font-size: 36px;
    font-weight: 900;
    color: #fff;
    margin: 0 0 8px;
  }

  .hero-subtitle {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
  }

  // Page Container
  .page-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 40px 24px;
  }

  // Filter Tabs
  .filter-tabs {
    display: flex;
    gap: 12px;
    margin-bottom: 32px;
    flex-wrap: wrap;
  }

  .filter-tab {
    height: 36px;
    padding: 0 20px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    border: 1px solid #d1d5db;
    background: #fff;
    color: #4b5563;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      border-color: #0066ff;
      color: #0066ff;
    }

    &.active {
      background: #0066ff;
      color: #fff;
      border-color: #0066ff;
    }
  }

  // News List
  .news-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .news-item {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
    cursor: pointer;
    display: flex;
    gap: 16px;
    align-items: flex-start;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      border-color: #0066ff;
    }
  }

  .news-date-block {
    flex-shrink: 0;
    text-align: center;
    width: 64px;
  }

  .news-day {
    font-size: 28px;
    font-weight: 800;
    color: #0066ff;
    line-height: 1;
    margin: 0;
  }

  .news-month {
    font-size: 12px;
    color: #9ca3af;
    margin: 4px 0 0;
  }

  .news-content {
    flex: 1;
    min-width: 0;
  }

  .news-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #111827;
    line-height: 1.4;
  }

  .news-summary {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 12px;
    line-height: 1.8;
  }

  .news-footer {
    display: flex;
    align-items: center;
  }

  .news-date-tag {
    font-size: 12px;
    color: #6b7280;
    background: #f3f4f6;
    padding: 2px 10px;
    border-radius: 4px;
  }
}
</style>
